import { format, isSameDay, isYesterday, parseISO } from "date-fns";

export function isValidBusinessTimezone(timeZone) {
	if (!timeZone || typeof timeZone !== "string") return false;
	const t = timeZone.trim();
	if (!t) return false;
	try {
		Intl.DateTimeFormat(undefined, { timeZone: t }).format(new Date());
		return true;
	} catch {
		return false;
	}
}

function normalizeApiDateString(value) {
	if (value == null || value === "") return null;
	if (typeof value !== "string") return String(value);
	const s = value.trim();
	if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}/.test(s) && !/[zZ]|[+-]\d{2}:?\d{2}$/.test(s)) {
		return s.replace(" ", "T");
	}
	return s;
}

function hasExplicitOffset(s) {
	return /[zZ]|[+-]\d{2}:?\d{2}$/.test(s);
}

function wallPartsInZone(date, timeZone) {
	const dtf = new Intl.DateTimeFormat("en-US", {
		timeZone,
		hour12: false,
		year: "numeric",
		month: "2-digit",
		day: "2-digit",
		hour: "2-digit",
		minute: "2-digit",
		second: "2-digit",
	});
	const parts = dtf.formatToParts(date);
	const get = (type) => parts.find((p) => p.type === type)?.value;
	return {
		y: Number(get("year")),
		mo: Number(get("month")),
		d: Number(get("day")),
		h: Number(get("hour")),
		mi: Number(get("minute")),
		se: Number(get("second")),
	};
}

function wallTimeInZoneToDate(target, timeZone) {
	const utcGuess = Date.UTC(
		target.y,
		target.mo - 1,
		target.d,
		target.h,
		target.mi,
		target.se
	);
	let low = utcGuess - 48 * 60 * 60 * 1000;
	let high = utcGuess + 48 * 60 * 60 * 1000;

	for (let i = 0; i < 50; i++) {
		const mid = Math.floor((low + high) / 2);
		const p = wallPartsInZone(new Date(mid), timeZone);
		const cmp =
			p.y !== target.y
				? p.y - target.y
				: p.mo !== target.mo
					? p.mo - target.mo
					: p.d !== target.d
						? p.d - target.d
						: p.h !== target.h
							? p.h - target.h
							: p.mi !== target.mi
								? p.mi - target.mi
								: p.se - target.se;
		if (cmp === 0) return new Date(mid);
		if (cmp < 0) low = mid + 1;
		else high = mid - 1;
	}
	return new Date(utcGuess);
}

function parseNaiveWallClock(s, timeZone) {
	const m = s.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})/);
	if (!m) return parseISO(s);
	return wallTimeInZoneToDate(
		{
			y: Number(m[1]),
			mo: Number(m[2]),
			d: Number(m[3]),
			h: Number(m[4]),
			mi: Number(m[5]),
			se: Number(m[6]),
		},
		timeZone
	);
}

export function toDateFromApi(value, timeZone = null) {
	if (value == null || value === "") return null;
	if (value instanceof Date) return value;

	const normalized = normalizeApiDateString(value);
	if (!normalized) return null;

	try {
		if (hasExplicitOffset(normalized)) {
			return parseISO(normalized);
		}
		if (timeZone && isValidBusinessTimezone(timeZone)) {
			return parseNaiveWallClock(normalized, timeZone);
		}
		return parseISO(normalized);
	} catch {
		return null;
	}
}

function calendarKeyInZone(date, timeZone) {
	return new Intl.DateTimeFormat("en-CA", {
		timeZone,
		year: "numeric",
		month: "2-digit",
		day: "2-digit",
	}).format(date);
}

export function formatMessageClock(isoOrDate, timeZone = null) {
	const d = toDateFromApi(isoOrDate, timeZone);
	if (!d || Number.isNaN(d.getTime())) return "";

	if (timeZone && isValidBusinessTimezone(timeZone)) {
		return new Intl.DateTimeFormat("pt-BR", {
			timeZone,
			hour: "2-digit",
			minute: "2-digit",
			hour12: false,
		}).format(d);
	}
	return format(d, "HH:mm");
}

export function formatMessageCalendarDay(isoOrDate, timeZone = null) {
	const d = toDateFromApi(isoOrDate, timeZone);
	if (!d || Number.isNaN(d.getTime())) return "";

	if (timeZone && isValidBusinessTimezone(timeZone)) {
		return new Intl.DateTimeFormat("pt-BR", {
			timeZone,
			day: "2-digit",
			month: "2-digit",
			year: "numeric",
		}).format(d);
	}
	return format(d, "dd/MM/yyyy");
}

export function isSameWallDayInZone(isoOrDateA, isoOrDateB, timeZone = null) {
	const a = toDateFromApi(isoOrDateA, timeZone);
	const b = toDateFromApi(isoOrDateB, timeZone);
	if (!a || !b || Number.isNaN(a.getTime()) || Number.isNaN(b.getTime())) return false;

	if (timeZone && isValidBusinessTimezone(timeZone)) {
		return calendarKeyInZone(a, timeZone) === calendarKeyInZone(b, timeZone);
	}
	return isSameDay(a, b);
}

export function isMessageSameLocalDay(
	isoOrDate,
	referenceDate = new Date(),
	timeZone = null
) {
	return isSameWallDayInZone(isoOrDate, referenceDate, timeZone);
}

export function isMessageYesterday(isoOrDate, referenceDate = new Date(), timeZone = null) {
	const d = toDateFromApi(isoOrDate, timeZone);
	if (!d || Number.isNaN(d.getTime())) return false;

	if (timeZone && isValidBusinessTimezone(timeZone)) {
		const yesterday = new Date(referenceDate.getTime() - 24 * 60 * 60 * 1000);
		return calendarKeyInZone(d, timeZone) === calendarKeyInZone(yesterday, timeZone);
	}
	return isYesterday(d);
}
